import { useState } from 'react'
import { errorHandler } from '@/helpers/utils'
import type {
	EditExperiencePayload,
	ShellPartialFormSchema
} from '../form-schemas'
import type { httpEditExperienceOptions } from '../http/experience-shell.http'
import { useEditExperience } from '../queries/use-edit-experience'
import { useEditStep } from '../queries/use-edit-step'
import { useGetExperience } from '../queries/use-get-experience'
import type { Experience } from '../types'
import { ShellStepKeyName } from '../types'
import { makeShellFormDefaultValues } from '../utils'
import { useExpItinerariesPosition } from './use-exp-itineraries-position'
import { useExperienceShellFormContext } from './use-experience-shell-form-context'

export const useChangeStep = ({
	experienceId,
	currentStep,
	isUpgrade
}: {
	experienceId: number
	currentStep: ShellStepKeyName
	isUpgrade: boolean
}) => {
	const [isBackPending, setIsBackPending] = useState(false)
	const { getValues, trigger, reset } =
		useExperienceShellFormContext(isUpgrade)
	const { data: experienceData } = useGetExperience(experienceId)

	const editStepMutation = useEditStep({
		experienceId,
		options: {
			onError: (error) => errorHandler(error),
			onSuccess: (data: Experience) => {
				reset(
					makeShellFormDefaultValues(data) as ShellPartialFormSchema
				)
			}
		}
	})
	const editExperienceMutation = useEditExperience({
		experienceId,
		options: { onError: (error) => errorHandler(error) }
	})
	const itineraryPositionMutation = useExpItinerariesPosition({
		urlParams: { experienceId },
		options: { onError: (error) => errorHandler(error) }
	})
	const isBackLoading =
		isBackPending &&
		(editStepMutation.isPending ||
			editExperienceMutation.isPending ||
			itineraryPositionMutation.isPending)
	const isContinueLoading =
		!isBackPending &&
		(editStepMutation.isPending ||
			editExperienceMutation.isPending ||
			itineraryPositionMutation.isPending)

	const stepToEditStepKeyName = (step: ShellStepKeyName) => {
		if (step === ShellStepKeyName.enum.pricing_discounts) {
			return 'pricing-discounts'
		}
		return step
	}

	const getPayload = (): httpEditExperienceOptions['payload'] => {
		if (currentStep === ShellStepKeyName.enum.photos) {
			return {}
		}
		if (currentStep === ShellStepKeyName.enum.background) {
			const values = getValues(currentStep)
			return {
				...values,
				online_profile_links: values.online_profile_links
					?.filter((link) => !!link.link)
					.map((link) => link.link)
			} as EditExperiencePayload
		}
		if (currentStep === ShellStepKeyName.enum.legal) {
			const values = getValues(currentStep)
			const { categoryKeyName: _categoryKeyName, ...rest } = values
			return rest as EditExperiencePayload
		}
		return getValues(currentStep) as EditExperiencePayload
	}

	const updateItinerariesPositions = async () => {
		if (experienceData?.itineraries) {
			const itineraries = experienceData.itineraries || []
			if (itineraries.length > 0) {
				const positionsPayload = itineraries.map(
					(itinerary: { id: number }, index: number) => ({
						id: itinerary.id,
						position: index + 1
					})
				)
				await itineraryPositionMutation.mutateAsync(positionsPayload)
			}
		}
	}

	const handleChangeStep = async ({
		isBack,
		stepToGoTo
	}: {
		isBack?: boolean
		stepToGoTo: ShellStepKeyName
	}) => {
		if (isUpgrade) {
			const isValid =
				currentStep !== ShellStepKeyName.enum.photos
					? await trigger(currentStep)
					: true
			if (!isValid) {
				return
			}
		}
		await updateItinerariesPositions()
		setIsBackPending(!!isBack)

		await editExperienceMutation.mutateAsync(getPayload())
		await editStepMutation.mutateAsync(stepToEditStepKeyName(stepToGoTo))
		setIsBackPending(false)
	}

	return {
		editStepMutation,
		editExperienceMutation,
		itineraryPositionMutation,
		isBackLoading,
		isContinueLoading,
		handleChangeStep,
		updateItinerariesPositions
	}
}
